const GAME_TITLE_TOKEN = "{gameTitle}";

/** Whether one naming template writes the game title, and so lets it be edited. */
export function templateUsesGameTitle(template) {
  return typeof template === "string" && template.includes(GAME_TITLE_TOKEN);
}

function collectTemplates(buildProfileData) {
  const templates = [buildProfileData.outputPath, buildProfileData.exportName];

  Object.values(buildProfileData).forEach((networkSettings) => {
    if (networkSettings && typeof networkSettings === "object") {
      templates.push(networkSettings.folderName, networkSettings.htmlName, networkSettings.scriptName);
    }
  });

  return templates;
}

export function usesGameTitleInExportNaming(buildProfileData) {
  if (!buildProfileData || typeof buildProfileData !== "object") return false;

  return collectTemplates(buildProfileData).some(
    (template) => typeof template === "string" && template.includes(GAME_TITLE_TOKEN)
  );
}

/**
 * A build profile's noFolder flag only survives when the build turns out to be a single html
 * file. The server reports the networks it had to cancel it for; this turns them into one
 * aggregated warning. Returns null when there is nothing to warn about.
 */
export function noFolderCancelledMessage(networks) {
  const unique = [...new Set((networks || []).filter(Boolean))];
  if (!unique.length) return null;

  return `noFolder was cancelled for ${unique.join(", ")} — the build produced more than a single HTML file.`;
}

export function resolveExportGameTitle(buildProfileData, gameTitleOverride, fallbackGameTitle) {
  if (!usesGameTitleInExportNaming(buildProfileData)) return fallbackGameTitle;

  const trimmedOverride = String(gameTitleOverride || "").trim();
  return trimmedOverride || fallbackGameTitle;
}

/**
 * Whether this company delivers its exports to Dropbox.
 *
 * The build profile's `dropboxPath` is both the destination and the switch: a
 * company without one has no Dropbox at all, and the server refuses to deliver
 * for it — so the dialogs must not offer the choice either.
 */
/**
 * The name an export will be delivered under, from the profile's `exportName`.
 *
 * Mirrors the server's resolveExportName (controller/version/uploadGame.js) so
 * the export dialog can show the name before the build rather than after it.
 * Keep the two token sets agreeing.
 */
export function resolveExportFileName(
  buildProfileData,
  { languages = [], adNetworks = [], versionName = "", gameTitle = "", baseTitle = "", timeStamp = "" }
) {
  const template = buildProfileData && buildProfileData.exportName;
  if (!template) return null;

  const joinedLanguages = languages.join("_");
  const vars = {
    timeStamp: String(timeStamp),
    versionName: String(versionName).replace(/ /g, "-"),
    languages: joinedLanguages,
    adNetworks: adNetworks.join("_"),
    gameTitle: String(gameTitle).replace(/ /g, "-"),
    baseTitle: String(baseTitle).replace(/ /g, "-"),
    languages_c: joinedLanguages.toUpperCase(),
    languages_C: joinedLanguages.toUpperCase(),
  };
  return template.replace(/\{(\w+)\}/g, (match, key) => (vars[key] === undefined ? match : vars[key]));
}

export function deliversToDropbox(buildProfileData) {
  return Boolean(
    buildProfileData &&
    typeof buildProfileData === "object" &&
    typeof buildProfileData.dropboxPath === "string" &&
    buildProfileData.dropboxPath.trim()
  );
}

/**
 * The Dropbox folder this export will land in, for the confirmation dialog.
 *
 * Resolved from the same `dropboxPath` template the server uses, so what the
 * person approves is what gets written. The server resolves it again rather
 * than trusting this — a destination the browser can edit is a destination
 * anyone can redirect — so the two must agree, and that is what keeps them
 * honest: a mismatch is visible the moment anyone looks at the result.
 */
export function dropboxDestination(buildProfileData, { dropboxFolder, gameTitle, versionName }) {
  if (!deliversToDropbox(buildProfileData)) return null;

  // The server composes with spaces turned to dashes (ExportComposer), so the
  // preview has to do the same or it shows a path that is not the one written.
  const vars = {
    dropboxFolder: dropboxFolder || gameTitle,
    gameTitle: String(gameTitle || "").replace(/ /g, "-"),
    versionName,
  };
  const resolved = String(buildProfileData.dropboxPath).replace(
    /\{(\w+)\}/g,
    (match, key) => (vars[key] == null ? match : vars[key])
  );
  return `/${resolved.split("/").filter(Boolean).join("/")}`;
}
